import axios from "axios";

export interface RequestItem {
  id: string;
  companyName: string;
  inn: string;
  url: string;
  accepted?: boolean;
  available?: boolean;
  lastPing?: string | null;
  status: string;
  schedule?: TaskSchedule[];
}

export interface CreateRequestPayload {
  companyName: string;
  inn: string;
  url: string;
  status: "new";
}

export interface EditRequestPayload {
  companyName: string;
  inn: string;
  url: string;
}

export interface RequestListParams {
  queryString: string;
  page: number;
  pageSize: number;
  status: string[] | null;
  direction: string;
}

const requestByIdInFlight = new Map<string, Promise<RequestItem>>();

export const fetchRequestList = (params: RequestListParams): Promise<RequestItem[]> => {
  return axios.post("/request/list", params).then((resp) => resp.data);
};

export const fetchRequestById = (id: string): Promise<RequestItem> => {
  const inFlight = requestByIdInFlight.get(id);
  if (inFlight) return inFlight;

  const requestPromise = axios
    .get(`/request/${id}`)
    .then((resp) => resp.data)
    .finally(() => {
      requestByIdInFlight.delete(id);
    });

  requestByIdInFlight.set(id, requestPromise);
  return requestPromise;
};

export const createRequest = (data: CreateRequestPayload): Promise<RequestItem> => {
  return axios.post("/request", data).then((resp) => resp.data);
};

export const editRequest = (id: string, data: EditRequestPayload): Promise<RequestItem> => {
  return axios.put(`/request/${id}`, data).then((resp) => resp.data);
};

export interface ScheduleRequestResponse {
  status?: string;
}

export const scheduleRequest = (
  id: string,
  scheduleDate: string
): Promise<ScheduleRequestResponse> => {
  return axios
    .post(`/request/${id}/schedule`, { scheduleDate })
    .then((resp) => (resp.data ?? {}) as ScheduleRequestResponse);
};

export interface TaskListItem {
  id: string;
  requestId: string;
  startDateTime: string;
  endDateTime: string | null;
  status: string;
}

export type TaskRowColor = "green" | "red" | "yellow" | null;

export interface TaskDetailItem {
  taskId: string;
  checkName: string;
  checkNameDescription: string;
  result: string;
  resultDescription: string;
  details?: string;
  suggestion: string;
  responsibility: string;
  color?: TaskRowColor;
}

export interface TaskDetailResponse {
  requestId: string;
  startDateTime: string;
  endDateTime: string | null;
  status?: string;
  tasks: TaskDetailItem[];
}

export interface TaskListPageResponse {
  items: TaskListItem[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export type SchedulePeriod = "daily" | "weekly" | "monthly";

export interface ScheduleConfig {
  period: SchedulePeriod;
  weekDay?: number;
  day?: number;
}

export interface TaskSchedule {
  id: string;
  requestId?: string;
  itCheckRequestId?: string;
  schedule: ScheduleConfig;
  createdAt?: string;
  nextRunAt?: string;
  lastRunAt?: string;
  status?: "active" | "paused" | "cancelled";
}

export interface TaskScheduleUpsertPayload {
  requestId?: string;
  itCheckRequestId?: string;
  schedule: ScheduleConfig;
}

export type CheckDetailKind = "site" | "it";

export const fetchRequestTaskList = (id: string): Promise<TaskListItem[]> => {
  return axios.get(`/request/${id}/task`).then((resp) => resp.data);
};

export const fetchRequestTaskListPage = (
  id: string,
  page: number,
  pageSize: number,
  direction: "asc" | "desc"
): Promise<TaskListPageResponse> => {
  return axios
    .get(`/request/${id}/task`, {
      params: {
        page,
        pageSize,
        direction,
      },
    })
    .then((resp) => {
      const headers = resp.headers ?? {};
      const getHeaderNumber = (key: string, fallback: number): number => {
        const raw = headers[key.toLowerCase()];
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      return {
        items: Array.isArray(resp.data) ? resp.data : [],
        page: getHeaderNumber("X-Page", page),
        pageSize: getHeaderNumber("X-Page-Size", pageSize),
        totalPages: getHeaderNumber("X-Total-Pages", 1),
        totalElements: getHeaderNumber("X-Total-Elements", Array.isArray(resp.data) ? resp.data.length : 0),
      };
    });
};

export const fetchRequestTaskDetail = (id: string, taskId: string): Promise<TaskDetailResponse> => {
  return axios.get(`/request/${id}/task/${taskId}`).then((resp) => resp.data);
};

export const createTaskSchedule = (data: TaskScheduleUpsertPayload): Promise<TaskSchedule> => {
  return axios.post("/schedule", data).then((resp) => resp.data);
};

export const fetchTaskScheduleById = (id: string): Promise<TaskSchedule> => {
  return axios.get(`/schedule/${id}`).then((resp) => resp.data);
};

export const updateTaskSchedule = (id: string, data: TaskScheduleUpsertPayload): Promise<TaskSchedule> => {
  return axios.put(`/schedule/${id}`, data).then((resp) => resp.data);
};

export const deleteTaskSchedule = (id: string): Promise<void> => {
  return axios.delete(`/schedule/${id}`).then(() => {});
};
